import { cn } from "@/lib/utils";
import wave from "@/assets/illustrations/wave.svg";
import calm from "@/assets/illustrations/calm.svg";
import creativity from "@/assets/illustrations/creativity.svg";
import heartInHands from "@/assets/illustrations/heart-in-hands.svg";
import holdingHands from "@/assets/illustrations/holding-hands.svg";
import laptop from "@/assets/illustrations/laptop.svg";
import money from "@/assets/illustrations/money.svg";
import snickers from "@/assets/illustrations/snickers.svg";
import studies from "@/assets/illustrations/studies.svg";
import team from "@/assets/illustrations/team.svg";

export type PracticeIllustration =
  | "calm"
  | "creativity"
  | "heart-in-hands"
  | "holding-hands"
  | "laptop"
  | "money"
  | "snickers"
  | "studies"
  | "team";

const ILLUSTRATIONS: Record<PracticeIllustration, string> = {
  calm,
  creativity,
  "heart-in-hands": heartInHands,
  "holding-hands": holdingHands,
  laptop,
  money,
  snickers,
  studies,
  team,
};

export type ThematicPracticeCardProps = {
  title: string;
  description: string;
  duration: string;
  illustration: PracticeIllustration;
  ctaLabel?: string;
  onStart?: () => void;
  className?: string;
};

export function ThematicPracticeCard({
  title,
  description,
  duration,
  illustration,
  ctaLabel = "Start",
  onStart,
  className,
}: ThematicPracticeCardProps) {
  const illustrationSrc = ILLUSTRATIONS[illustration];

  return (
    <div
      className={cn(
        "relative h-[260px] w-[195px] overflow-hidden rounded-2xl bg-background-sand",
        "shadow-[0_10px_15px_-3px_rgba(67,67,67,0.1),0_4px_6px_-4px_rgba(0,0,0,0.1)]",
        className,
      )}
    >
      {/* Top illustration area with green wave background */}
      <div className="absolute inset-x-0 top-0 h-[110px] overflow-hidden">
        <img
          src={wave}
          alt=""
          aria-hidden="true"
          className="absolute top-0 left-0 w-full scale-[1.32]"
        />
        {/* lazy keeps React SSR from preloading every card's illustration
            ahead of the hero image (the LCP element) */}
        <img
          src={illustrationSrc}
          alt={`${title} illustration`}
          loading="lazy"
          decoding="async"
          className="absolute inset-0 h-full w-full object-contain"
        />
      </div>

      {/* Duration badge */}
      <div className="absolute left-[14px] top-[14px] flex items-center rounded-[15px] border border-white/10 bg-white/30 px-[13px] py-[7px] backdrop-blur-md">
        <span className="font-sans text-[12px] font-medium leading-4 text-white">
          {duration}
        </span>
      </div>

      {/* Content */}
      <div className="absolute inset-x-0 bottom-0 flex h-[176px] flex-col items-start justify-end gap-2 px-5 pb-5 pt-12">
        <p className="w-full font-display text-[20px] font-semibold leading-7 tracking-[-0.04px] text-text-primary">
          {title}
        </p>
        <p className="w-full pb-2 font-sans text-[16px] font-light leading-[22px] text-text-secondary">
          {description}
        </p>
        <div className="flex h-10 w-full items-center justify-center rounded-full bg-primary-500 px-4 py-2 font-sans text-[18px] leading-[26px] text-white">
          {ctaLabel}
        </div>
      </div>
    </div>
  );
}

export default ThematicPracticeCard;